import { useCallback } from "react";
import useSWR from "swr";
import { SiteSurveySlotsResponse } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  INSTALL_AVAILABLE_SLOTS_QUERY,
  INSTALL_AVAILABLE_SLOTS_WINDOW_DAYS,
} from "../constants/capInstallScheduling";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";
import { useAuthToken } from "hooks/useAuthToken";
import {
  getInstallAvailableSlots,
  type GetInstallAvailableSlotsParams,
} from "services/installScheduling";

const INSTALL_AVAILABLE_SLOTS_DEDUPING_INTERVAL_MS = 1000 * 60 * 10;

/**
 * Shares one Salesforce slots promise if SWR invokes the fetcher twice for the
 * same params while the first call is still in flight.
 */
const inflightInstallSlotsByFetcherKey = new Map<
  string,
  Promise<SiteSurveySlotsResponse>
>();

const resetInstallAvailableSlotsInflightForTests = () => {
  inflightInstallSlotsByFetcherKey.clear();
};

/**
 * FSL caps a single search at 30 days. If the first window is empty, search
 * the next 30 days at startOffset + windowDays.
 */
const fetchInstallAvailableSlotsWithNextWindowFallback = async (
  params: GetInstallAvailableSlotsParams
) => {
  const firstWindow = await getInstallAvailableSlots(params);
  if (firstWindow.length > 0) {
    return firstWindow;
  }
  return getInstallAvailableSlots({
    ...params,
    startOffset: params.startOffset + params.windowDays,
  });
};

/**
 * FSL install slots via available-service-slots (async WS via subscribeToAsyncRequest).
 * Fetch starts only after `hasRequestedSlots` is true (CAP session flag).
 */
const useInstallAvailableServiceSlots = (
  prospectId: string | undefined,
  serviceAppointmentId: string | undefined,
  startOffset: number | undefined,
  consecutiveScheduling?: boolean,
  hasRequestedSlots = false
) => {
  const authKey = useAuthToken("HYBRID");

  const canFetch =
    prospectId &&
    serviceAppointmentId &&
    authKey?.token &&
    startOffset !== undefined &&
    hasRequestedSlots;

  const windowDays = INSTALL_AVAILABLE_SLOTS_WINDOW_DAYS;

  const { data, error, mutate } = useSWR(
    canFetch
      ? [
          "Get Available Service Slots",
          prospectId,
          serviceAppointmentId,
          startOffset,
          windowDays,
          consecutiveScheduling,
          authKey.token,
          authKey.type,
        ]
      : null,
    () => {
      const inflightKey = [
        prospectId,
        serviceAppointmentId,
        startOffset,
        windowDays,
        consecutiveScheduling,
      ].join(":");
      const existing = inflightInstallSlotsByFetcherKey.get(inflightKey);
      if (existing) {
        return existing;
      }
      const request = fetchInstallAvailableSlotsWithNextWindowFallback({
        authToken: authKey!.token,
        authType: authKey!.type,
        prospectId: prospectId!,
        serviceAppointmentId: serviceAppointmentId!,
        startOffset: startOffset!,
        windowDays,
        ...INSTALL_AVAILABLE_SLOTS_QUERY,
        ...(consecutiveScheduling ? { consecutiveScheduling: "true" } : {}),
      });
      const tracked = request.finally(() => {
        inflightInstallSlotsByFetcherKey.delete(inflightKey);
      });
      inflightInstallSlotsByFetcherKey.set(inflightKey, tracked);
      return tracked;
    },
    {
      dedupingInterval: INSTALL_AVAILABLE_SLOTS_DEDUPING_INTERVAL_MS,
      errorRetryCount: 0,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  const retry = useCallback(
    () => mutate(undefined, { revalidate: true }),
    [mutate]
  );

  return {
    data,
    isLoading: hasRequestedSlots && data === undefined && !error,
    error,
    retry,
  } as const;
};

export {
  resetInstallAvailableSlotsInflightForTests,
  useInstallAvailableServiceSlots,
};
export type { SiteSurveySlot };
